import type { ReactNode } from 'react';
import type { Consulta } from '../api/useConsulta.js';
import { Cartao } from './Cartao.js';
import { FalhaDeCarregamento } from './FalhaDeCarregamento.js';
import './Resultado.css';

type Props<T> = {
  readonly consulta: Consulta<T>;
  /** O que se está esperando, em uma frase ("Carregando movimentações…"). */
  readonly espera: string;
  readonly estaVazio: (dados: T) => boolean;
  readonly vazio: ReactNode;
  readonly children: (dados: T) => ReactNode;
};

/**
 * Os quatro desfechos de uma consulta, na mesma ordem em toda tela do painel:
 * falhou, ainda não voltou, voltou vazia, voltou com dado.
 *
 * A falha aparece **no lugar** da tabela, com caminho de volta. Tabela vazia
 * por erro de rede é indistinguível de armazém parado, e o supervisor
 * decidiria em cima disso; estado vazio afirma que não há registro, e afirmar
 * isso sem ter conseguido perguntar seria mentir sobre o armazém.
 *
 * Antes cada tela resolvia isto por conta própria: Movimentações mostrava a
 * falha como um parágrafo sem saída, Usuários e Dispositivos ofereciam
 * "Tentar de novo", e nenhuma das duas dizia que estava carregando.
 */
export function Resultado<T>({ consulta, espera, estaVazio, vazio, children }: Props<T>) {
  if (consulta.erro !== null) {
    return <FalhaDeCarregamento mensagem={consulta.erro} aoTentarDeNovo={consulta.recarregar} />;
  }

  if (consulta.dados === null) {
    return (
      <Cartao semRespiro>
        <p className="resultado__aviso" role="status">
          {espera}
        </p>
      </Cartao>
    );
  }

  if (estaVazio(consulta.dados)) {
    return <Cartao semRespiro>{vazio}</Cartao>;
  }

  return <>{children(consulta.dados)}</>;
}
